import { useRef, useEffect } from "react";
import { usePlayerStore } from "@/lib/player";
import { MiniPlayer } from "./MiniPlayer";
import { ExpandedPlayer } from "./ExpandedPlayer";
import { PublicPreviewPlayer } from "./PublicPreviewPlayer";
import { useAuth } from "@/providers/AuthProvider";
import { useHeldListeningPath } from "@/hooks/useListeningPath";
import { AuthModal } from "@/components/auth/AuthModal";
import { AppEntryButton } from "@/components/auth/AppEntryButton";
import { useToast } from "@/hooks/use-toast";
import { DOWNLOAD_REFUSED_TITLE, downloadRefusalSentence } from "@/lib/download-access";

type PlayerChoice = "clean" | "public";

/**
 * Which player plays the store's track (`useHeldListeningPath`): the app's player and the clean
 * file for a fully set-up account, the public preview player for everyone else, neither while that
 * is not known. Held through the "loading" right after signing in, so the public preview player
 * that was playing stays until the account's profile has been read.
 */
function usePlayerChoice(): PlayerChoice | null {
  const path = useHeldListeningPath();
  return path === "loading" ? null : path;
}

export function PlayerProvider({ children }: { children: React.ReactNode }) {
  const currentTrack = usePlayerStore((state) => state.currentTrack);
  const isExpanded = usePlayerStore((state) => state.isExpanded);
  const showAuthModal = usePlayerStore((state) => state.showAuthModal);
  const setShowAuthModal = usePlayerStore((state) => state.setShowAuthModal);
  const authModalReason = usePlayerStore((state) => state.authModalReason);
  const executePendingDownload = usePlayerStore(
    (state) => state.executePendingDownload
  );
  const audioRef = useRef<HTMLAudioElement>(null);
  const { user } = useAuth();
  const { toast } = useToast();
  const pauseTrack = usePlayerStore((state) => state.pauseTrack);
  const clearTrack = usePlayerStore((state) => state.clearTrack);
  const player = usePlayerChoice();

  // Seeks asked for outside the player (the storefront's now-playing block). This element is the
  // app's player's; PublicPreviewPlayer applies the same request to its own.
  const seekRequest = usePlayerStore((state) => state.seekRequest);
  useEffect(() => {
    const audio = audioRef.current;
    if (!seekRequest || !audio || !Number.isFinite(audio.duration) || audio.duration <= 0) return;
    audio.currentTime = (seekRequest.position / 100) * audio.duration;
  }, [seekRequest]);

  // Pause and reset audio when user logs out
  useEffect(() => {
    if (!user && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      pauseTrack();
      clearTrack();
    }
  }, [user, pauseTrack, clearTrack]);

  // The shared element carries the clean file, and only while the app's player owns it: whenever
  // the public preview player plays instead (or nothing does yet), it is silent.
  useEffect(() => {
    if (player !== "clean") audioRef.current?.pause();
  }, [player]);

  // A download asked for behind the sign-in gate runs once the visitor has signed in. Listening
  // has no gate: a track the public preview player was playing is taken over by the app's player
  // once the account is known to be fully set up.
  const handleAuthSuccess = async () => {
    const pendingDownload = usePlayerStore.getState().pendingDownload;
    if (pendingDownload) {
      const result = await executePendingDownload();
      // Signed in, but not as a fully set-up account (N12): the backend refused it, and says why.
      if (result && !result.ok && result.refusal) {
        toast({
          title: DOWNLOAD_REFUSED_TITLE,
          description: downloadRefusalSentence(result.refusal),
          variant: "destructive",
        });
      } else if (result?.ok) {
        toast({
          title: "Download started",
          description: `${pendingDownload.track.title} is downloading`,
        });
      } else {
        toast({
          title: "Download failed",
          description: `Failed to download ${pendingDownload.track.title}. Please try again.`,
          variant: "destructive",
        });
      }
    }
  };

  return (
    <>
      {/* Soft-tier join invitation — mounted once, globally, as a SIBLING of `children` (Layout),
          not a child inside its `h-screen` flex column. It self-gates to public/share surfaces
          only (see isPublicShareSurface in AppEntryButton.tsx): those pages use
          `min-h-screen` and absorb the strip's height correctly, whereas every authenticated page
          under Layout's hard `h-screen` shell would instead grow a page-level scrollbar on the
          whole app. Global mounting is still needed so it can appear regardless of which
          AuthModal instance the visitor signed up through. */}
      <AppEntryButton />
      {children}
      {/* Shared audio element */}
      <audio
        key={currentTrack ? String(currentTrack.id) : "audio"}
        ref={audioRef}
        crossOrigin="anonymous"
        preload="metadata"
        style={{ display: "none" }}
      />
      {/* The app's player, clean audio, for a fully set-up account; the public preview (the whole
          track) for everyone else. Never both. */}
      {player === "public" && <PublicPreviewPlayer />}
      {player === "clean" &&
        currentTrack &&
        (isExpanded ? (
          <ExpandedPlayer
            key={`player-expanded-${String(currentTrack.id)}`}
            audioRef={audioRef}
          />
        ) : (
          <MiniPlayer
            key={`player-mini-${String(currentTrack.id)}`}
            audioRef={audioRef}
          />
        ))}
      {/* Sign-in for a gated download, the preview player's "Sign in", and the rest of the app's
          sign-in prompts. */}
      <AuthModal
        open={showAuthModal}
        onOpenChange={(open) => setShowAuthModal(open, authModalReason)}
        reason={authModalReason}
        onAuthSuccess={handleAuthSuccess}
      />
    </>
  );
}
